import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { authService } from "@/services";
import { tokenManager } from "@/lib/axios";
import { ROUTES } from "@/utils/constants";
import toast from "react-hot-toast";
import { useStore } from "@/stores/app-store";
import { getApiErrorMessage } from "@/utils/api";
import { canAccessWeb } from "@/utils/permissions";

export const useAuth = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser, setUser } = useStore();

  const { data: user, isLoading } = useQuery({
    queryKey: ["currentUser"],
    queryFn: authService.getCurrentUser,
    retry: false,
    staleTime: 1000 * 60 * 5,
    enabled: tokenManager.hasToken(),
  });

  const isAuthenticated = !!user && tokenManager.hasToken();

  const loginMutation = useMutation({
    mutationFn: authService.login,
    onSuccess: (data) => {
      if (!canAccessWeb(data.user.role)) {
        toast.error(
          "Votre compte n'a pas accès à l'application web. Utilisez l'application mobile.",
        );
        return;
      }
      tokenManager.setToken(data.accessToken);
      setUser(data.user);
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      toast.success(data.message || "Connexion réussie");
      navigate(ROUTES.DASHBOARD);
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, "Échec de la connexion"));
    },
  });

  const logout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error("Logout API call failed:", error);
    } finally {
      tokenManager.clearToken();
      queryClient.clear();
      navigate(ROUTES.AUTH.LOGIN);
      toast.success("Déconnexion réussie");
    }
  };

  return {
    user,
    isAuthenticated,
    isLoading,
    login: loginMutation.mutate,
    logout,
    isLoginLoading: loginMutation.isPending,
    loginError: loginMutation.error,
  };
};
